import React, { FC } from 'react';
import { UseQueryResult, useQuery, useQueryClient } from 'react-query';

import { AxiosResponse } from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import Page from '../components/Page/Page';
import { permConst } from '../constants/permConst';
import {
	CHANGE_COMPANY_PROFILE_LOAD_STATUS,
	CHANGE_HELP_TOPICS_LOAD_STATUS,
	CHANGE_PLATFORM_LOAD_STATUS,
	CHANGE_SUNSET_FEATURES_LOAD_STATUS,
	SET_COMPANY_PROFILE,
	SET_HELP_TOPICS,
	SET_PLATFORMS,
	SET_SUNSET_FEATURES,
} from '../store/actions/action-types';
import { changeLoadStatus, setData } from '../store/actions/referenceActions';
import { RootState } from '../store/store';
import { isAuthorized } from '../utils/userUtils';
import { useUserProfile } from './reduxHooks';
import {
	getCompanyAgreements,
	getCompanyProfile,
	getPlatforms,
	getSunsetFeatures,
	getHelpTopics,
} from '../services';


export const useCompanyProfileQuery = (
	options?: Record<string, any>,
): UseQueryResult<AxiosResponse<{ company_information: CompanyProfile } | null, any>, unknown> => {
	const userProfile = useUserProfile();
	const companyId = userProfile?.companyId || '';
	const dispatch = useDispatch();
	const optionsWithDefaults = {
		enabled: !!companyId,
		staleTime: Infinity,
		initialData: useSelector((state: RootState) => {
			if (state.referenceReducer?.companyProfile?.content) {
				return { data: state.referenceReducer?.companyProfile?.content };
			}
			return undefined;
		}),
		onSuccess: (response: AxiosResponse) => {
			if (response.status === 200) {
				dispatch(setData(SET_COMPANY_PROFILE, response.data));
				dispatch(
					changeLoadStatus(
						CHANGE_COMPANY_PROFILE_LOAD_STATUS,
						'COMPLETED',
						response.headers['x-correlation-id'],
					),
				);
			}
		},
		...options,
	};
	const authorized =
		userProfile &&
		isAuthorized(userProfile.permissions, [
			permConst.COMPANY.VIEW.ALL,
			permConst.COMPANY.VIEW.COMPANY,
		]);
	return useQuery(
		['getCompanyProfile', companyId],
		() => (companyId ? getCompanyProfile(companyId) : null),
		authorized ? optionsWithDefaults : { enabled: false },
	);
};
export const useCompanyAgreementsQuery = (options?: Record<string, any>) => {
	const userProfile = useUserProfile();
	const optionsWithDefaults = { staleTime: Infinity, ...options };
	const authorized = userProfile && isAuthorized(userProfile.permissions, [
		permConst.COMPANY.VIEW.ALL,
		permConst.COMPANY.VIEW.COMPANY,
	]);
	return useQuery(
		['getCompanyAgreements', userProfile.companyId],
		() => getCompanyAgreements(userProfile.companyId),
		authorized ?  optionsWithDefaults : { enabled: false }
	);
};
export const usePlatformsQuery = (
	options?: Record<string, any>,
) => {
	const authorized = !!useUserProfile();
	const dispatch = useDispatch();
	const optionsWithDefaults = { 
		staleTime: Infinity, 
		initialData: useSelector((state: RootState) => {
			if (state.referenceReducer?.platforms?.content) {
				return { data: state.referenceReducer?.platforms?.content };
			}
			return undefined;
		}),
		onSuccess: (response: AxiosResponse) => {
			if (response.status === 200) {
				dispatch(setData(SET_PLATFORMS, response.data));
				dispatch(
					changeLoadStatus(
						CHANGE_PLATFORM_LOAD_STATUS,
						'COMPLETED',
						response.headers['x-correlation-id'],
					),
				);
			}
		},
		...options
	};
	return useQuery(
		'getPlatforms',
		() => getPlatforms(),
		authorized ? optionsWithDefaults : { enabled: false }
	);
};
export const useHelpTopicsQuery = (
	options?: Record<string, any>,
) => {
	const authorized = !!useUserProfile();
	const dispatch = useDispatch();
	const optionsWithDefaults = { 
		staleTime: Infinity, 
		initialData: useSelector((state: RootState) => {
			if (state.referenceReducer?.helpTopics?.content) {
				return { data: state.referenceReducer?.helpTopics?.content };
			}
			return undefined;
		}),
		onSuccess: (response: AxiosResponse) => {
			if (response.status === 200) {
				dispatch(setData(SET_HELP_TOPICS, response.data));
				dispatch(
					changeLoadStatus(
						CHANGE_HELP_TOPICS_LOAD_STATUS,
						'COMPLETED',
						response.headers['x-correlation-id'],
					),
				);
			}
		},
		...options
	};
	return useQuery(
		'getHelpTopics',
		() => getHelpTopics(),
		authorized ? optionsWithDefaults : { enabled: false }
	);
};
export const useSunsetFeaturesQuery = (
	options?: Record<string, any>,
) => {
	const authorized = !!useUserProfile();
	const dispatch = useDispatch();
	const optionsWithDefaults = { 
		staleTime: Infinity, 
		initialData: useSelector((state: RootState) => {
			if (state.referenceReducer?.sunsetFeatures?.content) {
				return { data: state.referenceReducer.sunsetFeatures?.content };
			}
			return undefined;
		}),
		onSuccess: (response: AxiosResponse) => {
			if (response.status === 200) {
				dispatch(setData(SET_SUNSET_FEATURES, response.data));
				dispatch(
					changeLoadStatus(
						CHANGE_SUNSET_FEATURES_LOAD_STATUS,
						'COMPLETED',
						response.headers['x-correlation-id'],
					),
				);
			}
		},
		...options
	};
	return useQuery(
		'getSunsetFeatures',
		() => getSunsetFeatures() as any,
		authorized ? optionsWithDefaults : { enabled: false }
	);
};


export const connectQueryClient = <T extends JSX.IntrinsicAttributes = {}>(
	Component: FC<T>,
) => {
	const ConnectQueryClientHOCComponent: FC<T> = (props: T) => {
		const queryClient = useQueryClient();
		return <Component {...{ ...props, queryClient }} />;
	};
	return ConnectQueryClientHOCComponent;
};

export const connectQueryData = <T extends JSX.IntrinsicAttributes = {}, D = unknown, E = unknown>(
	Component: FC<T>,
	useQueryHook: () => UseQueryResult<AxiosResponse<D>, E>,
	propName: string,
	isPageComponent?: boolean
) => {
	const ConnectQueryDataHOCComponent: FC<T> = (props: T) => {
		const query = useQueryHook();
		return !query.isLoading && !query.isFetching ? (
			<Component {...{ ...props, [propName]: query.data?.data }} />
		) : isPageComponent ? (
			<Page isLoading={true} />
		) : (
			null
		);
	};
	return ConnectQueryDataHOCComponent;
};
